import type { ComponentProps } from "react";

import NextLink from "next/link";
import { buttonVariants } from "@heroui/react/button";
import { cn } from "@heroui/react";

/**
 * A link that looks like a button.
 *
 * HeroUI's `Button` is built on React Aria's Button, which deliberately does
 * not accept `href` — and nesting a `<button>` inside an `<a>` is invalid
 * markup that breaks middle-click, "open in new tab" and screen readers. So
 * navigation targets get a real anchor wearing the button's styles.
 */
export function ButtonLink({
  href,
  variant = "primary",
  size = "md",
  fullWidth,
  className,
  children,
  ...rest
}: {
  href: string;
  variant?: "primary" | "secondary" | "tertiary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
} & Omit<ComponentProps<typeof NextLink>, "href">) {
  return (
    <NextLink
      href={href}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      {...rest}
    >
      {children}
    </NextLink>
  );
}
